import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  createPlaceCategory,
  fetchPlaceTypes,
  fetchBusinessUnitsByDomain,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function PlaceCategoryCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [placeTypes, setPlaceTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [businessUnitId, setBusinessUnitId] = useState(null);
  const [form, setForm] = useState({
    placeCategoryName: "",
    placeCategoryDescription: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [types, units] = await Promise.all([
          fetchPlaceTypes(businessDomainId),
          fetchBusinessUnitsByDomain(businessDomainId),
        ]);
        setPlaceTypes(Array.isArray(types) ? types : []);
        const unit = Array.isArray(units) ? units[0] : null;
        setBusinessUnitId(unit?.businessUnitId || null);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypesChange = (e) => {
    const values = e.target.value;
    setSelectedTypes(typeof values === "string" ? [] : values);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPlaceCategory({
        companyId: COMPANY_ID,
        businessUnitId,
        placeCategoryName: form.placeCategoryName,
        placeCategoryDescription: form.placeCategoryDescription || null,
        associatedPlaceTypes: selectedTypes.map((pt) => ({
          placeTypeId: pt.placeTypeId,
          placeTypeName: pt.placeTypeName,
        })),
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/place-categories`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("placeCategory.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required
              label={t("placeCategory.name")}
              name="placeCategoryName"
              value={form.placeCategoryName}
              onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("placeCategory.placeTypes")}</InputLabel>
              <Select
                multiple
                value={selectedTypes}
                onChange={handleTypesChange}
                input={<OutlinedInput label={t("placeCategory.placeTypes")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v.placeTypeId} label={v.placeTypeName} size="small" />
                    ))}
                  </Box>
                )}
              >
                {placeTypes.map((pt) => (
                  <MenuItem key={pt.placeTypeId} value={pt}>
                    {pt.placeTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3}
              label={t("common.description")}
              name="placeCategoryDescription"
              value={form.placeCategoryDescription}
              onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/place-categories`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
